import { useState } from "react";
import { Copy, Check, Heart, Share2, ExternalLink, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { resolveImageUrl } from "@/lib/image";

const CATEGORY_COLORS = {
  Cyberpunk: "text-[#00E5FF] bg-[#00E5FF]/10 border-[#00E5FF]/20",
  Corporate: "text-white/80 bg-white/5 border-white/10",
  Editorial: "text-white/80 bg-white/5 border-white/10",
  Anime: "text-[#E0007A] bg-[#E0007A]/10 border-[#E0007A]/25",
  Vintage: "text-amber-200 bg-amber-200/10 border-amber-200/20",
  Minimalist: "text-white/70 bg-white/5 border-white/10",
  Street: "text-[#00E5FF] bg-[#00E5FF]/10 border-[#00E5FF]/20",
  Fantasy: "text-violet-300 bg-violet-300/10 border-violet-300/20",
  Cottagecore: "text-amber-100 bg-amber-100/10 border-amber-100/20",
};

/**
 * Parse a lightweight "Key: Value, Key: Value" or JSON-ish parameters string
 * into an ordered array of {key, value} for badge rendering. Never throws.
 */
function parseParameters(input) {
  if (!input || typeof input !== "string") return [];
  const raw = input.trim();
  if (!raw) return [];
  // Try JSON first
  if (raw.startsWith("{")) {
    try {
      const obj = JSON.parse(raw);
      return Object.entries(obj).map(([k, v]) => ({ key: k, value: String(v) }));
    } catch {
      /* fall through */
    }
  }
  return raw
    .split(",")
    .map((p) => p.trim())
    .filter(Boolean)
    .map((p) => {
      const idx = p.indexOf(":");
      if (idx === -1) return { key: "Tag", value: p };
      return {
        key: p.slice(0, idx).trim(),
        value: p.slice(idx + 1).trim(),
      };
    })
    .filter((x) => x.value);
}

export default function PromptCard({ prompt, onLikeUpdate }) {
  const [copied, setCopied] = useState(false);
  const [likes, setLikes] = useState(prompt.likes_count);
  const [liked, setLiked] = useState(false);
  const [imgLoaded, setImgLoaded] = useState(false);

  const catStyle =
    CATEGORY_COLORS[prompt.style_category] || "text-white/70 bg-white/5 border-white/10";

  const handleCopy = async () => {
    await writeToClipboard(prompt.prompt_text);
    setCopied(true);
    toast.success("Prompt Copied! Paste into Gemini", {
      description: prompt.title,
      duration: 2500,
    });
    setTimeout(() => setCopied(false), 1800);
  };

  const writeToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
  };

  const handleOpenGemini = async () => {
    await writeToClipboard(prompt.prompt_text);
    setCopied(true);
    toast.success("Prompt copied! Redirecting to Gemini…", {
      description: prompt.title,
      duration: 2200,
    });
    setTimeout(() => setCopied(false), 1800);
    window.open("https://gemini.google.com/", "_blank", "noopener,noreferrer");
  };

  const handleLike = async () => {
    if (liked) return;
    setLiked(true);
    setLikes((n) => n + 1);
    try {
      const { data } = await api.post(`/prompts/${prompt.id}/like`);
      setLikes(data.likes_count);
      if (onLikeUpdate) onLikeUpdate(prompt.id, data.likes_count);
    } catch {
      setLiked(false);
      setLikes((n) => Math.max(0, n - 1));
    }
  };

  const shareUrl = `${window.location.origin}/prompt/${prompt.id}`;
  const shareText = `${prompt.title} — free AI profile prompt`;

  const openShare = (platform) => {
    const enc = encodeURIComponent;
    let url = "";
    if (platform === "twitter") {
      url = `https://twitter.com/intent/tweet?text=${enc(shareText)}&url=${enc(shareUrl)}`;
    } else if (platform === "pinterest") {
      url = `https://pinterest.com/pin/create/button/?url=${enc(shareUrl)}&media=${enc(
        prompt.image_url,
      )}&description=${enc(shareText)}`;
    }
    if (url) window.open(url, "_blank", "noopener,noreferrer,width=640,height=580");
  };

  return (
    <article
      data-testid={`prompt-card-${prompt.id}`}
      className="card-lift rounded-2xl overflow-hidden bg-[#0E0E12] border border-white/10"
    >
      <div className="px-5 pt-4 pb-3">
        <span
          data-testid={`prompt-category-label-${prompt.id}`}
          className={`inline-flex items-center text-sm md:text-base font-extrabold uppercase tracking-wide px-3 py-1.5 rounded-md border ${catStyle}`}
          style={{ letterSpacing: "0.06em" }}
        >
          {prompt.style_category}
        </span>
      </div>
      <Link to={`/prompt/${prompt.id}`} className="block relative">
        <div className="relative aspect-portrait bg-[#17171C]">
          {!imgLoaded && (
            <div className="absolute inset-0 animate-pulse bg-gradient-to-b from-white/[0.03] to-white/[0.01]" />
          )}
          <img
            src={resolveImageUrl(prompt.image_url)}
            alt={prompt.title}
            width={480}
            height={600}
            loading="lazy"
            decoding="async"
            onLoad={() => setImgLoaded(true)}
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${
              imgLoaded ? "opacity-100" : "opacity-0"
            }`}
          />
          <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-[#0E0E12] to-transparent pointer-events-none" />
        </div>
      </Link>

      <div className="p-5">
        <Link to={`/prompt/${prompt.id}`}>
          <h3 className="font-display text-xl tracking-tight leading-tight line-clamp-2 hover:text-[#00E5FF] transition-colors">
            {prompt.title}
          </h3>
        </Link>

        <p
          data-testid={`prompt-explanation-${prompt.id}`}
          className="mt-3 text-sm text-white/70 leading-relaxed line-clamp-3 md:line-clamp-4 min-h-[3.75rem]"
        >
          {prompt.explanation_text}
        </p>

        <Link
          to={`/prompt/${prompt.id}`}
          data-testid={`view-prompt-btn-${prompt.id}`}
          className="mt-4 w-full inline-flex items-center justify-center gap-2 font-semibold text-sm rounded-full py-3 px-4 bg-[#E0007A] hover:bg-[#ff1493] text-white glow-magenta transition-transform duration-200 hover:scale-[1.02]"
        >
          View Prompt
          <ArrowRight className="h-4 w-4" />
        </Link>

        <button
          data-testid={`copy-prompt-btn-${prompt.id}`}
          onClick={handleCopy}
          className={`mt-2 w-full inline-flex items-center justify-center gap-2 font-medium text-sm rounded-full py-2.5 px-4 transition-all duration-200 border ${
            copied
              ? "bg-white text-[#050505] border-white"
              : "border-white/15 text-white/80 hover:text-white hover:border-white/40"
          }`}
        >
          {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
          {copied ? "Copied — paste into Gemini" : "Click to Copy Prompt"}
        </button>

        <button
          data-testid={`open-gemini-btn-${prompt.id}`}
          onClick={handleOpenGemini}
          className="mt-2 w-full inline-flex items-center justify-center gap-2 font-medium text-sm rounded-full py-2.5 px-4 border border-white/15 hover:border-[#00E5FF]/60 text-white/80 hover:text-[#00E5FF] hover:bg-[#00E5FF]/[0.04] transition-colors"
        >
          <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" aria-hidden="true">
            <defs>
              <linearGradient id={`gemini-grad-${prompt.id}`} x1="0" y1="0" x2="24" y2="24" gradientUnits="userSpaceOnUse">
                <stop offset="0%" stopColor="#1BA1E3" />
                <stop offset="50%" stopColor="#7B68EE" />
                <stop offset="100%" stopColor="#E0007A" />
              </linearGradient>
            </defs>
            <path
              d="M12 2c.3 4.5 2.5 6.7 7 7-4.5.3-6.7 2.5-7 7-.3-4.5-2.5-6.7-7-7 4.5-.3 6.7-2.5 7-7Z"
              fill={`url(#gemini-grad-${prompt.id})`}
            />
          </svg>
          Open in Gemini
          <ExternalLink className="h-3 w-3 opacity-60" />
        </button>

        <div className="mt-4 flex items-center justify-between">
          <button
            data-testid={`like-btn-${prompt.id}`}
            onClick={handleLike}
            className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-[#E0007A] transition-colors"
          >
            <Heart
              className={`h-4 w-4 ${liked ? "fill-[#E0007A] text-[#E0007A] heart-pop" : ""}`}
            />
            <span className="font-mono text-xs">{likes}</span>
          </button>

          <div className="flex items-center gap-1">
            <button
              data-testid={`share-twitter-${prompt.id}`}
              onClick={() => openShare("twitter")}
              className="p-2 rounded-md text-white/60 hover:text-white hover:bg-white/5 transition-colors"
              aria-label="Share on X/Twitter"
              title="Share on X"
            >
              <svg viewBox="0 0 24 24" className="h-4 w-4 fill-current">
                <path d="M18.244 2H21.5l-7.53 8.61L22.5 22h-6.79l-5.32-6.95L4.28 22H1l8.06-9.22L1.5 2h6.94l4.81 6.36L18.244 2Zm-1.19 18h1.87L7.03 4H5.07l11.984 16Z" />
              </svg>
            </button>
            <button
              data-testid={`share-pinterest-${prompt.id}`}
              onClick={() => openShare("pinterest")}
              className="p-2 rounded-md text-white/60 hover:text-white hover:bg-white/5 transition-colors"
              aria-label="Share on Pinterest"
              title="Share on Pinterest"
            >
              <svg viewBox="0 0 24 24" className="h-4 w-4 fill-current">
                <path d="M12.04 2C6.51 2 2 6.51 2 12.04c0 4.23 2.62 7.85 6.32 9.31-.09-.79-.17-2 .04-2.86.19-.78 1.23-4.95 1.23-4.95s-.31-.63-.31-1.55c0-1.45.84-2.53 1.88-2.53.89 0 1.32.67 1.32 1.47 0 .9-.57 2.24-.87 3.48-.25 1.04.52 1.89 1.55 1.89 1.87 0 3.3-1.97 3.3-4.82 0-2.52-1.81-4.28-4.4-4.28-3 0-4.76 2.25-4.76 4.57 0 .91.35 1.88.79 2.41.09.1.1.19.07.29-.08.32-.25 1.04-.29 1.19-.05.19-.15.24-.35.15-1.32-.61-2.14-2.53-2.14-4.08 0-3.32 2.41-6.37 6.96-6.37 3.65 0 6.49 2.6 6.49 6.08 0 3.63-2.29 6.55-5.47 6.55-1.07 0-2.07-.55-2.41-1.21l-.66 2.5c-.24.92-.88 2.08-1.31 2.78.99.3 2.03.47 3.13.47 5.53 0 10.04-4.51 10.04-10.04C22.08 6.51 17.57 2 12.04 2Z" />
              </svg>
            </button>
            <button
              data-testid={`share-copy-link-${prompt.id}`}
              onClick={() => {
                navigator.clipboard.writeText(shareUrl);
                toast("Share link copied");
              }}
              className="p-2 rounded-md text-white/60 hover:text-white hover:bg-white/5 transition-colors"
              aria-label="Copy share link"
              title="Copy share link"
            >
              <Share2 className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </article>
  );
}
